import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { PhoneIcon } from '../icons';
import './QrCard.css';

export interface CaptureLink {
  /** Default Drive. Koofr adds `p=koofr` (and `u=` for a non-default host) and hands the phone this computer's sign-in. */
  provider?: 'drive' | 'koofr';
  /** A Drive folder id, or the Koofr Inkorg path. */
  inboxFolderId: string;
  companyName: string;
  koofrBaseUrl?: string;
  /** The desktop's Koofr sign-in, so the phone needs no typing. The QR then holds a whole-account credential; the card says so. */
  koofrEmail?: string;
  koofrAppPassword?: string;
  /** Unix ms expiry. */
  expiresAt: number;
  /** Origin, default window.location.origin. */
  origin?: string;
}

/** `https://<host>/kamera.html#v=1&f=<inbox>&c=<name>&e=<expiry>[&p=koofr&u=<host>&m=<email>&k=<app password>]` — the hash never reaches a server. */
export function buildCaptureUrl(link: CaptureLink): string {
  const origin = link.origin ?? (typeof window !== 'undefined' ? window.location.origin : '');
  const params = new URLSearchParams({ v: '1', f: link.inboxFolderId, c: link.companyName, e: String(link.expiresAt) });
  if (link.provider === 'koofr') {
    params.set('p', 'koofr');
    if (link.koofrBaseUrl) params.set('u', link.koofrBaseUrl);
    if (link.koofrEmail && link.koofrAppPassword) {
      params.set('m', link.koofrEmail);
      params.set('k', link.koofrAppPassword);
    }
  }
  return `${origin}/kamera.html#${params.toString()}`;
}

/** Parse the hash of a capture URL; null when malformed or not v1. */
export function parseCaptureHash(hash: string): Omit<CaptureLink, 'origin'> | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const p = new URLSearchParams(raw);
  if (p.get('v') !== '1') return null;
  const f = p.get('f');
  const c = p.get('c') ?? '';
  const e = Number(p.get('e'));
  if (!f || !Number.isFinite(e)) return null;
  const provider = p.get('p') === 'koofr' ? 'koofr' : 'drive';
  const u = p.get('u');
  const m = p.get('m');
  const k = p.get('k');
  return {
    provider,
    inboxFolderId: f,
    companyName: c,
    expiresAt: e,
    ...(provider === 'koofr' && u ? { koofrBaseUrl: u } : {}),
    ...(provider === 'koofr' && m && k ? { koofrEmail: m, koofrAppPassword: k } : {}),
  };
}

export interface QrCardProps {
  /** The URL to encode; null renders the explanatory placeholder (no cloud storage connected). */
  value: string | null;
  /** `null` hides the heading, for nesting inside a card that already has one. */
  title?: string | null;
  body?: string;
  /** Shown when `value` is null. */
  placeholder?: React.ReactNode;
  /** Drop the card chrome when nested inside another card. */
  bare?: boolean;
  className?: string;
}

/** QR card: "Fota med mobilen." Renders the code to a canvas via the qrcode package. */
export function QrCard({ value, title = 'Fota med mobilen.', body = 'Öppna kameran på telefonen och rikta den mot koden. Koden gäller i en vecka; öppna menyn igen för en ny.', placeholder, bare, className }: QrCardProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !value) return;
    let cancelled = false;
    QRCode.toCanvas(canvas, value, { errorCorrectionLevel: 'M', margin: 1, width: 192, color: { dark: '#16201a', light: '#ffffff' } })
      .then(() => {
        if (!cancelled) setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [value]);

  return (
    <div className={['qr-card', bare ? 'qr-card-bare' : '', className ?? ''].filter(Boolean).join(' ')}>
      <div className="qr-card-code" aria-label={value ? 'QR-kod för Kvittokameran' : undefined} role={value ? 'img' : undefined}>
        {value ? <canvas ref={canvasRef} width={192} height={192} /> : <div className="qr-card-placeholder"><PhoneIcon size={28} /></div>}
      </div>
      <div className="qr-card-text">
        {title ? <h3 className="qr-card-title">{title}</h3> : null}
        <p className="qr-card-body">{value ? body : placeholder ?? 'Anslut Google Drive eller Koofr för att kunna fota kvitton med mobilen.'}</p>
        {error ? <p className="field-error">Kunde inte rita koden. Försök igen.</p> : null}
      </div>
    </div>
  );
}
